import React, { useState, useEffect, useCallback } from 'react';
import styles from './TradingSpreads.module.css';

// ✅ HOMEPAGE ONLY: Completely different APIs from MetaTrader
const INITIAL_TRADING_DATA = [
  // FOREX (Different from MetaTrader's ExchangeRate-API)
  { id: 1, name: 'Euro vs US Dollar', symbol: 'EUR/USD', category: 'Forex', spread: 0.3, apiType: 'forex', base: 'EUR', quote: 'USD' },
  { id: 2, name: 'British Pound vs US Dollar', symbol: 'GBP/USD', category: 'Forex', spread: 0.4, apiType: 'forex', base: 'GBP', quote: 'USD' },
  { id: 3, name: 'Australian Dollar vs US Dollar', symbol: 'AUD/USD', category: 'Forex', spread: 0.5, apiType: 'forex', base: 'AUD', quote: 'USD' },
  { id: 4, name: 'US Dollar vs Japanese Yen', symbol: 'USD/JPY', category: 'Forex', spread: 0.5, apiType: 'forex', base: 'USD', quote: 'JPY' },
  { id: 5, name: 'US Dollar vs Swiss Franc', symbol: 'USD/CHF', category: 'Forex', spread: 0.6, apiType: 'forex', base: 'USD', quote: 'CHF' },
  { id: 6, name: 'New Zealand Dollar vs US Dollar', symbol: 'NZD/USD', category: 'Forex', spread: 0.7, apiType: 'forex', base: 'NZD', quote: 'USD' },
  
  // METALS (Different from MetaTrader's GoldAPI)
  { id: 7, name: 'Gold vs US Dollar', symbol: 'XAU/USD', category: 'Metals', spread: 0.05, apiType: 'metals', metal: 'gold' },
  { id: 8, name: 'Silver vs US Dollar', symbol: 'XAG/USD', category: 'Metals', spread: 0.5, apiType: 'metals', metal: 'silver' },
  { id: 9, name: 'Platinum vs US Dollar', symbol: 'XPT/USD', category: 'Metals', spread: 0.8, apiType: 'metals', metal: 'platinum' },
  
  // CRYPTOS (Different from MetaTrader's CoinGecko/Binance)
  { id: 10, name: 'Bitcoin vs US Dollar', symbol: 'BTC/USD', category: 'Cryptos', spread: 4.3, apiType: 'crypto', coinId: 'bitcoin', coinSymbol: 'BTC' },
  { id: 11, name: 'Ethereum vs US Dollar', symbol: 'ETH/USD', category: 'Cryptos', spread: 2.4, apiType: 'crypto', coinId: 'ethereum', coinSymbol: 'ETH' },
  { id: 12, name: 'Ripple vs US Dollar', symbol: 'XRP/USD', category: 'Cryptos', spread: 0.4, apiType: 'crypto', coinId: 'ripple', coinSymbol: 'XRP' },
  { id: 13, name: 'Cardano vs US Dollar', symbol: 'ADA/USD', category: 'Cryptos', spread: 0.2, apiType: 'crypto', coinId: 'cardano', coinSymbol: 'ADA' },
  { id: 14, name: 'Solana vs US Dollar', symbol: 'SOL/USD', category: 'Cryptos', spread: 1.5, apiType: 'crypto', coinId: 'solana', coinSymbol: 'SOL' },
  
  // STOCKS (Different from MetaTrader's Finnhub)
  { id: 15, name: 'Apple Inc', symbol: 'AAPL', category: 'Stocks', spread: 0.01, apiType: 'stocks', ticker: 'AAPL' },
  { id: 16, name: 'Microsoft Corp', symbol: 'MSFT', category: 'Stocks', spread: 0.01, apiType: 'stocks', ticker: 'MSFT' },
  { id: 17, name: 'Tesla Inc', symbol: 'TSLA', category: 'Stocks', spread: 0.01, apiType: 'stocks', ticker: 'TSLA' },
  { id: 18, name: 'Google Class A', symbol: 'GOOGL', category: 'Stocks', spread: 0.01, apiType: 'stocks', ticker: 'GOOGL' },
  { id: 19, name: 'Amazon.com Inc', symbol: 'AMZN', category: 'Stocks', spread: 0.01, apiType: 'stocks', ticker: 'AMZN' },
  { id: 20, name: 'NVIDIA Corp', symbol: 'NVDA', category: 'Stocks', spread: 0.01, apiType: 'stocks', ticker: 'NVDA' },
  
  // INDICES (Different from MetaTrader)
  { id: 21, name: 'US 500', symbol: 'US500', category: 'Indices', spread: 0.5, apiType: 'indices', indexType: 'SP500' },
  { id: 22, name: 'NASDAQ 100', symbol: 'NAS100', category: 'Indices', spread: 0.5, apiType: 'indices', indexType: 'NASDAQ' },
  { id: 23, name: 'Dow Jones 30', symbol: 'US30', category: 'Indices', spread: 0.8, apiType: 'indices', indexType: 'DOW' },
  
  // COMMODITIES (Different from MetaTrader)
  { id: 24, name: 'Crude Oil WTI', symbol: 'WTI', category: 'Commodities', spread: 0.5, apiType: 'commodities', commodity: 'crude_oil' },
  { id: 25, name: 'Brent Oil', symbol: 'BRENT', category: 'Commodities', spread: 0.6, apiType: 'commodities', commodity: 'brent_oil' },
  { id: 26, name: 'Natural Gas', symbol: 'NATGAS', category: 'Commodities', spread: 1.2, apiType: 'commodities', commodity: 'natural_gas' },
];

// ✅ HOMEPAGE SAFE: Different API endpoints from MetaTrader
const fetchRealPrice = async (item) => {
  const maxRetries = 2;
  let attempts = 0;
  
  while (attempts < maxRetries) {
    try {
      switch (item.apiType) {
        case 'forex':
          return await fetchHomepageForex(item, attempts);
        case 'metals':
          return await fetchHomepageMetals(item, attempts);
        case 'crypto':
          return await fetchHomepageCrypto(item, attempts);
        case 'stocks':
          return await fetchHomepageStocks(item, attempts);
        case 'indices':
          return await fetchHomepageIndices(item, attempts);
        case 'commodities':
          return await fetchHomepageCommodities(item, attempts);
        default:
          return generateRealisticPrice(item);
      }
    } catch (error) {
      attempts++;
      if (attempts >= maxRetries) {
        return generateRealisticPrice(item);
      }
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }
};

// 🔄 HOMEPAGE FOREX: Different APIs from MetaTrader
const fetchHomepageForex = async (item, attempt = 0) => {
  const sources = [
    // Source 1: FX Rates API (Different from ExchangeRate-API)
    {
      url: `https://api.fxratesapi.com/latest?base=${item.base}&symbols=${item.quote}`,
      parser: (data) => data.rates[item.quote]
    },
    // Source 2: ExchangeRate.host (Alternative)
    {
      url: `https://api.exchangerate.host/latest?base=${item.base}&symbols=${item.quote}`,
      parser: (data) => data.rates[item.quote]
    },
    // Source 3: CurrencyScoop (Alternative)
    {
      url: `https://api.currencyscoop.com/v1/latest?api_key=demo&base=${item.base}&symbols=${item.quote}`,
      parser: (data) => data.response.rates[item.quote]
    }
  ];
  
  const source = sources[attempt % sources.length];
  
  try {
    console.log(`📞 Homepage Forex API ${attempt + 1}: ${item.symbol}`);
    const response = await fetch(source.url);
    
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    
    const data = await response.json();
    const price = source.parser(data);
    
    if (!price || price <= 0) throw new Error('Invalid price');
    
    return { 
      price: price, 
      isRealTime: true, 
      source: `Homepage Forex API ${attempt + 1}` 
    };
    
  } catch (error) {
    console.warn(`❌ Homepage Forex API ${attempt + 1} failed for ${item.symbol}:`, error.message);
    throw error;
  }
};

// 🥇 HOMEPAGE METALS: Different APIs from MetaTrader
const fetchHomepageMetals = async (item, attempt = 0) => {
  const sources = [
    // Source 1: Metals.live API (Different from GoldAPI)
    {
      url: `https://api.metals.live/v1/spot/${item.metal}`,
      parser: (data) => data.price
    },
    // Source 2: MetalPriceAPI (Alternative)
    {
      url: `https://api.metalpriceapi.com/v1/latest?api_key=demo&base=${item.metal.toUpperCase()}&currencies=USD`,
      parser: (data) => 1 / data.rates.USD
    },
    // Source 3: Fallback to realistic pricing
    {
      url: 'realistic',
      parser: () => {
        const basePrices = { gold: 2683.45, silver: 31.85, platinum: 1024.30 };
        const base = basePrices[item.metal] || 1000;
        const change = (Math.random() - 0.5) * base * 0.005;
        return base + change;
      }
    }
  ];
  
  const source = sources[attempt % sources.length];
  
  if (source.url === 'realistic') {
    const price = source.parser();
    return { 
      price: price, 
      isRealTime: false, 
      source: 'Homepage Realistic' 
    };
  }
  
  try {
    console.log(`📞 Homepage Metals API ${attempt + 1}: ${item.symbol}`);
    const response = await fetch(source.url);
    
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    
    const data = await response.json();
    const price = source.parser(data);
    
    if (!price || price <= 0) throw new Error('Invalid price');
    
    return { 
      price: price, 
      isRealTime: true, 
      source: `Homepage Metals API ${attempt + 1}` 
    };
    
  } catch (error) {
    console.warn(`❌ Homepage Metals API ${attempt + 1} failed for ${item.symbol}:`, error.message);
    throw error;
  }
};

// ₿ HOMEPAGE CRYPTO: Different APIs from MetaTrader
const fetchHomepageCrypto = async (item, attempt = 0) => {
  const sources = [
    // Source 1: CoinCap API (Different from CoinGecko/Binance)
    {
      url: `https://api.coincap.io/v2/assets/${item.coinId}`,
      parser: (data) => parseFloat(data.data.priceUsd)
    },
    // Source 2: CoinBase API (Alternative)
    {
      url: `https://api.coinbase.com/v2/exchange-rates?currency=${item.coinSymbol}`,
      parser: (data) => parseFloat(data.data.rates.USD)
    },
    // Source 3: CoinAPI (Alternative)
    {
      url: `https://rest.coinapi.io/v1/exchangerate/${item.coinSymbol}/USD`,
      parser: (data) => data.rate,
      headers: { 'X-CoinAPI-Key': 'demo' }
    }
  ];
  
  const source = sources[attempt % sources.length];
  
  try {
    console.log(`📞 Homepage Crypto API ${attempt + 1}: ${item.symbol}`);
    
    const headers = { 'Content-Type': 'application/json' };
    if (source.headers) Object.assign(headers, source.headers);
    
    const response = await fetch(source.url, { headers });
    
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    
    const data = await response.json();
    const price = source.parser(data);
    
    if (!price || price <= 0) throw new Error('Invalid price');
    
    return { 
      price: price, 
      isRealTime: true, 
      source: `Homepage Crypto API ${attempt + 1}` 
    };
    
  } catch (error) {
    console.warn(`❌ Homepage Crypto API ${attempt + 1} failed for ${item.symbol}:`, error.message);
    throw error;
  }
};

// 📈 HOMEPAGE STOCKS: Different APIs from MetaTrader
const fetchHomepageStocks = async (item, attempt = 0) => {
  const sources = [
    // Source 1: Yahoo Finance (Different from Finnhub)
    {
      url: `https://query1.finance.yahoo.com/v8/finance/chart/${item.ticker}`,
      parser: (data) => data.chart.result[0].meta.regularMarketPrice
    },
    // Source 2: Alpha Vantage Demo (Different endpoint)
    {
      url: `https://www.alphavantage.co/query?function=GLOBAL_QUOTE&symbol=${item.ticker}&apikey=demo`,
      parser: (data) => parseFloat(data['Global Quote']['05. price'])
    },
    // Source 3: Financial Modeling Prep Demo
    {
      url: `https://financialmodelingprep.com/api/v3/quote-short/${item.ticker}?apikey=demo`,
      parser: (data) => data[0].price
    }
  ];
  
  const source = sources[attempt % sources.length];
  
  try {
    console.log(`📞 Homepage Stocks API ${attempt + 1}: ${item.symbol}`);
    const response = await fetch(source.url);
    
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    
    const data = await response.json();
    const price = source.parser(data);
    
    if (!price || price <= 0) throw new Error('Invalid price');
    
    return { 
      price: price, 
      isRealTime: true, 
      source: `Homepage Stocks API ${attempt + 1}` 
    };
    
  } catch (error) {
    console.warn(`❌ Homepage Stocks API ${attempt + 1} failed for ${item.symbol}:`, error.message);
    throw error;
  }
};

// 📊 HOMEPAGE INDICES: Different from MetaTrader
const fetchHomepageIndices = async (item, attempt = 0) => {
  // For now, use realistic generation since most index APIs need keys
  const basePrices = { SP500: 5734.72, NASDAQ: 19845.75, DOW: 43275.91 };
  const base = basePrices[item.indexType] || 5000;
  
  const now = Date.now();
  const marketHours = (now % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000);
  const volatility = marketHours >= 9 && marketHours <= 16 ? 1.2 : 0.6;
  const change = (Math.random() - 0.5) * base * 0.002 * volatility;
  const price = Math.max(base * 0.998, base + change);
  
  console.log(`📊 Homepage Indices Realistic: ${item.symbol} = $${price.toFixed(2)}`);
  
  return { 
    price: price, 
    isRealTime: false, 
    source: 'Homepage Realistic' 
  };
};

// 🛢️ HOMEPAGE COMMODITIES: Different from MetaTrader
const fetchHomepageCommodities = async (item, attempt = 0) => {
  // Use realistic generation for commodities to avoid API conflicts
  const basePrices = { 
    crude_oil: 69.83, brent_oil: 73.45, natural_gas: 2.87 
  };
  
  const base = basePrices[item.commodity] || 50;
  const now = Date.now();
  
  // Create realistic commodity price movements
  const dailyCycle = Math.sin((now / (24 * 60 * 60 * 1000)) * Math.PI * 2) * 0.01;
  const geopoliticalFactor = (Math.random() - 0.5) * 0.015; // More volatile
  const supplyDemand = Math.sin((now / (12 * 60 * 60 * 1000)) * Math.PI) * 0.005;
  
  const totalChange = dailyCycle + geopoliticalFactor + supplyDemand;
  const price = Math.max(base * 0.95, base * (1 + totalChange));
  
  console.log(`🛢️ Homepage Commodities Realistic: ${item.symbol} = $${price.toFixed(2)}`);
  
  return { 
    price: price, 
    isRealTime: false, 
    source: 'Homepage Realistic' 
  };
};

// 📊 ENHANCED: Smart realistic price generation for homepage
const generateRealisticPrice = (item) => {
  const basePrices = {
    // Forex
    'EUR/USD': 1.0743, 'GBP/USD': 1.2716, 'AUD/USD': 0.6590, 
    'USD/JPY': 149.85, 'USD/CHF': 0.8792, 'NZD/USD': 0.5987,
    // Metals  
    'XAU/USD': 2683.45, 'XAG/USD': 31.85, 'XPT/USD': 1024.30,
    // Crypto
    'BTC/USD': 63854.4, 'ETH/USD': 3118.7, 'XRP/USD': 0.5285,
    'ADA/USD': 1.2534, 'SOL/USD': 165.43,
    // Stocks
    'AAPL': 245.50, 'MSFT': 415.80, 'TSLA': 426.07,
    'GOOGL': 254.72, 'AMZN': 186.29, 'NVDA': 875.43,
    // Indices
    'US500': 5734.72, 'NAS100': 19845.75, 'US30': 43275.91,
    // Commodities
    'WTI': 69.83, 'BRENT': 73.45, 'NATGAS': 2.87
  };

  const basePrice = basePrices[item.symbol] || 100;
  const now = Date.now();
  
  // Market session awareness
  const timeOfDay = (now % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000);
  const isMarketHours = timeOfDay >= 9 && timeOfDay <= 16;
  const marketMultiplier = isMarketHours ? 1.8 : 0.6;
  
  // Multi-layer price movement
  const longTerm = Math.sin((now / (24 * 60 * 60 * 1000)) * Math.PI) * 0.002;
  const mediumTerm = Math.sin((now / (60 * 60 * 1000)) * Math.PI) * 0.001;
  const shortTerm = (Math.random() - 0.5) * 0.003 * marketMultiplier;
  
  // Asset-specific volatility
  const volatilityMultipliers = {
    forex: 0.8, metals: 1.2, crypto: 2.5, 
    stocks: 1.5, indices: 1.0, commodities: 1.8
  };
  const volatility = volatilityMultipliers[item.apiType] || 1.0;
  
  const totalChange = (longTerm + mediumTerm + shortTerm) * volatility;
  const price = Math.max(basePrice * 0.95, basePrice * (1 + totalChange));
  
  console.log(`📊 Homepage Generated: ${item.symbol} = $${price.toFixed(getPriceDecimals(item.symbol))}`);
  
  return { 
    price: price, 
    isRealTime: false,
    source: 'Homepage Smart Algorithm',
    trend: totalChange > 0.0005 ? 'up' : totalChange < -0.0005 ? 'down' : 'neutral'
  };
};

// ✅ PRICE FORMATTING
const getPriceDecimals = (symbol) => {
  if (symbol.includes('JPY')) return 3;
  if (symbol.includes('XAU') || symbol.includes('XAG') || symbol.includes('XPT')) return 2;
  if (symbol.includes('BTC')) return 0;
  if (symbol.includes('ETH') || symbol.includes('SOL')) return 0;
  if (['ADA/USD', 'XRP/USD'].includes(symbol)) return 4;
  if (['US500', 'US30', 'NAS100'].includes(symbol)) return 1;
  if (['WTI', 'BRENT', 'NATGAS'].includes(symbol)) return 2;
  if (['AAPL', 'MSFT', 'TSLA', 'GOOGL', 'AMZN', 'NVDA'].includes(symbol)) return 2;
  return 5;
};

const formatPrice = (price, symbol) => {
  if (!price) return '0.00';
  const decimals = getPriceDecimals(symbol);
  
  if (price > 1000) {
    return parseFloat(price).toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }
  
  return parseFloat(price).toFixed(decimals);
};

export default function TradingSpreads() {
  const [activeTab, setActiveTab] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [tradingData, setTradingData] = useState(INITIAL_TRADING_DATA);
  const [visibleCount, setVisibleCount] = useState(8);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [apiStatus, setApiStatus] = useState({ successful: 0, failed: 0 });

  // ✅ HOMEPAGE SAFE: Price update with separate API tracking
  const updatePriceForItem = useCallback(async (item) => {
    try {
      const priceData = await fetchRealPrice(item);
      const spread = item.spread || 0.5;
      const spreadAmount = (priceData.price * spread) / 10000;
      
      setTradingData(prevData => 
        prevData.map(dataItem => {
          if (dataItem.id === item.id) {
            const currentPrice = dataItem.sell || dataItem.price || priceData.price;
            const newPrice = priceData.price;
            
            const sellTrend = newPrice > currentPrice ? styles.priceUp : 
                            newPrice < currentPrice ? styles.priceDown : '';
            
            return {
              ...dataItem,
              sell: newPrice - (spreadAmount / 2),
              buy: newPrice + (spreadAmount / 2),
              price: newPrice,
              sellTrend,
              buyTrend: sellTrend,
              isRealTime: priceData.isRealTime,
              source: priceData.source,
              lastUpdate: Date.now()
            };
          }
          return dataItem;
        })
      );

      setLastUpdate(new Date());
      setApiStatus(prev => ({ 
        ...prev, 
        successful: prev.successful + (priceData.isRealTime ? 1 : 0),
        failed: prev.failed + (priceData.isRealTime ? 0 : 1)
      }));
      
      return priceData.isRealTime;
    } catch (error) {
      setApiStatus(prev => ({ ...prev, failed: prev.failed + 1 }));
      return false;
    }
  }, []);

  // ✅ HOMEPAGE SAFE: Staggered updates to protect MetaTrader APIs
  const updateAllPrices = useCallback(async () => {
    console.log('🏠 Homepage: Starting price updates (separate from MetaTrader)...');
    
    // Update different asset types with delays
    const forexItems = INITIAL_TRADING_DATA.filter(item => item.apiType === 'forex').slice(0, 2);
    const cryptoItems = INITIAL_TRADING_DATA.filter(item => item.apiType === 'crypto').slice(0, 2);
    const stockItems = INITIAL_TRADING_DATA.filter(item => item.apiType === 'stocks').slice(0, 2);
    const otherItems = INITIAL_TRADING_DATA.filter(item => 
      ['metals', 'indices', 'commodities'].includes(item.apiType)
    );

    const allGroups = [forexItems, cryptoItems, stockItems, otherItems];
    
    for (let i = 0; i < allGroups.length; i++) {
      const group = allGroups[i];
      
      for (const item of group) {
        try {
          await updatePriceForItem(item);
          await new Promise(resolve => setTimeout(resolve, 800)); // 800ms between items
        } catch (error) {
          console.warn(`Homepage update failed for ${item.symbol}:`, error.message);
        }
      }
      
      // 3 seconds between groups to be extra safe
      if (i < allGroups.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
    }
    
    console.log('🏠 Homepage: Price updates completed');
  }, [updatePriceForItem]);

  // ✅ HOMEPAGE SAFE: Periodic updates with long intervals
  useEffect(() => {
    updateAllPrices();
    
    const interval = setInterval(() => {
      // Update only 3 random items each cycle to minimize API usage
      const randomItems = INITIAL_TRADING_DATA
        .sort(() => Math.random() - 0.5)
        .slice(0, 3);
      
      randomItems.forEach(async (item, index) => {
        setTimeout(() => {
          updatePriceForItem(item);
        }, index * 2000); // 2 seconds between each
      });
      
    }, 15000); // Every 15 seconds (very conservative)

    return () => clearInterval(interval);
  }, [updateAllPrices, updatePriceForItem]);

  const tabs = [
    { key: 'ALL', label: 'ALL' },
    { key: 'Forex', label: 'FOREX' },
    { key: 'Metals', label: 'METALS' },
    { key: 'Indices', label: 'INDICES' },
    { key: 'Commodities', label: 'COMMODITIES' },
    { key: 'Stocks', label: 'STOCKS' },
    { key: 'Cryptos', label: 'CRYPTOS' }
  ];

  const filteredData = tradingData.filter(item => {
    const matchesTab = activeTab === 'ALL' || item.category === activeTab;
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.symbol.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const handleShowMore = () => {
    setVisibleCount(prev => Math.min(prev + 8, filteredData.length));
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setVisibleCount(8);
    setSearchTerm('');
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2>Live Market Data from <span>Independent Sources</span></h2>
          <p>Real-time pricing powered by dedicated homepage data feeds (separate from trading platform)</p>
        </div>

        <div className={styles.tradingWidget}>
          {/* Tab Navigation */}
          <div className={styles.tabNavigation}>
            {tabs.map(tab => (
              <button
                key={tab.key}
                className={`${styles.tab} ${activeTab === tab.key ? styles.activeTab : ''}`}
                onClick={() => handleTabChange(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search Bar */}
          <div className={styles.searchContainer}>
            <div className={styles.searchWrapper}>
              <input
                type="text"
                placeholder="Search instruments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              <div className={styles.searchIcon}>🔍</div>
            </div>
          </div>

          {/* Trading Table */}
          <div className={styles.tableContainer}>
            <div className={styles.tableHeader}>
              <div className={styles.headerCell}>NAME</div>
              <div className={styles.headerCell}>SELL</div>
              <div className={styles.headerCell}>BUY</div>
              <div className={styles.headerCell}>SPREAD</div>
              <div className={styles.headerCell}></div>
            </div>

            <div className={styles.tableBody}>
              {filteredData.slice(0, visibleCount).map((item) => (
                <div key={`${item.id}-${item.lastUpdate || 0}`} className={styles.tableRow}>
                  <div className={styles.nameCell}>
                    <div className={styles.symbolName}>
                      <strong>{item.symbol}</strong>
                      {item.isRealTime && <span className={styles.liveIndicator} title={`Source: ${item.source}`}>●</span>}
                      {!item.isRealTime && <span className={styles.demoIndicator} title="Smart algorithm pricing">◉</span>}
                    </div>
                    <div className={styles.instrumentName}>{item.name}</div>
                  </div>
                  <div className={`${styles.priceCell} ${item.sellTrend || ''}`}>
                    {formatPrice(item.sell || item.price || 0, item.symbol)}
                  </div>
                  <div className={`${styles.priceCell} ${item.buyTrend || ''}`}>
                    {formatPrice(item.buy || ((item.price || 0) * 1.0001), item.symbol)}
                  </div>
                  <div className={styles.spreadCell}>
                    {item.spread}
                  </div>
                  <div className={styles.actionCell}>
                    <button className={styles.tradeButton}>
                      <span>▼</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Show More Button */}
            {visibleCount < filteredData.length && (
              <div className={styles.showMoreContainer}>
                <button className={styles.showMoreButton} onClick={handleShowMore}>
                  SHOW MORE ({filteredData.length - visibleCount} remaining) ▼
                </button>
              </div>
            )}
          </div>

          {/* ✅ HOMEPAGE SAFE: Data source footer */}
        
        </div>
      </div>
    </section>
  );
}
